import type {PropsWithChildren} from 'react';
import {useEffect, useState, useCallback, useMemo} from 'react';

// eslint-disable-next-line @shopify/images-no-direct-imports
import fallback from '../images/fallback.svg';

import {FlagImagesContext} from './FlagImagesContext';

interface BundledFlags {
  [key: string]: string;
}

export function FlagImagesProvider({children}: PropsWithChildren) {
  const [bundledFlags, setBundledFlags] = useState<BundledFlags | undefined>();
  useEffect(() => {
    let isCurrent = true;
    async function loadFlags() {
      const bundledFlags = await import(
        /* webpackChunkName: 'bundledFlags' */ '../images'
      );

      if (isCurrent) {
        setBundledFlags(bundledFlags);
      }
    }
    loadFlags();
    return () => {
      isCurrent = false;
    };
  }, []);

  const getFlagImageSource = useCallback(
    (countryCode: string, regionCode?: string) => {
      if (!bundledFlags) {
        return undefined;
      }
      const countryCodeLowerCase = countryCode?.toLowerCase();
      const regionCodeLowercase = regionCode?.toLowerCase();

      const flagCode = regionCode
        ? `${countryCodeLowerCase}_${regionCodeLowercase}`
        : countryCodeLowerCase;

      if (flagCode in bundledFlags) {
        return bundledFlags[flagCode];
      }
      return fallback;
    },
    [bundledFlags],
  );

  const contextValue = useMemo(
    () => ({
      getFlagImageSource,
      loading: !bundledFlags,
    }),
    [getFlagImageSource, bundledFlags],
  );

  return (
    <FlagImagesContext.Provider value={contextValue}>
      {children}
    </FlagImagesContext.Provider>
  );
}
